import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CalendarDays, MapPin, Loader2, BookOpen, Quote } from "lucide-react";
import { api, IMAGES, PASTOR_NAME, MASTERCLASS_BANNER } from "@/lib/data";
import { BWCarousel } from "@/components/BWCarousel";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: "easeOut" },
  }),
};

const Hero = () => (
  <section className="relative pt-28 md:pt-32" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-8 items-center py-10 md:py-16">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="md:col-span-6 order-2 md:order-1"
      >
        <p className="overline text-terracotta" data-testid="hero-overline">
          Masterclass · Apostolic Fire · Spiritual Fatherhood
        </p>
        <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink">
          Chief Apostle <span className="italic text-terracotta">Leonard Lucas Jr.</span>
        </h1>
        <p className="mt-7 font-body text-lg text-ink-soft leading-relaxed max-w-xl" data-testid="hero-bio">
          As a spiritual father to countless pastors and ministry leaders, Chief Apostle
          Lucas's legacy continues to expand. Alongside his co-leader, Apostle Leonard
          Lucas III, he actively bridges the gap between generations, ensuring that the
          fire of the Holy Spirit and the integrity of apostolic leadership continue to
          impact the world today. Whether through his dynamic Sunday services, deep-dive
          Bible studies, or the Light City Church Podcast, his messages continue to
          challenge, empower, and ignite the body of Christ worldwide.
        </p>
        <blockquote
          className="mt-8 border-l-4 border-terracotta pl-6 py-1"
          data-testid="hero-quote"
        >
          <p className="font-heading italic text-2xl md:text-3xl leading-snug text-forest">
            "For what does it profit a man to be anointed once, yet die cold and
            disconnected from the fire of God?"
          </p>
          <cite className="mt-3 block overline not-italic text-ink-soft">
            — Chief Apostle Leonard Lucas Jr.
          </cite>
        </blockquote>
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <a
            href="#engagements"
            data-testid="hero-engagements-btn"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-white font-medium transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-forest"
          >
            Upcoming Engagements <ArrowUpRight size={18} />
          </a>
          <Link
            to="/chat"
            data-testid="hero-chat-btn"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-medium text-ink transition-all hover:border-ink hover:-translate-y-0.5"
          >
            Join the Conversation
          </Link>
        </div>

        <div className="mt-10 flex justify-center md:justify-start" data-testid="hero-masterclass-banner">
          <Link
            to="/register"
            data-testid="hero-masterclass-link"
            className="group block w-full max-w-[560px] rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/5 transition-all hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-terracotta"
          >
            <img
              src={MASTERCLASS_BANNER}
              alt="The Financial Masterclass — Register Now"
              className="w-full object-cover"
            />
            <div className="bg-terracotta text-white text-center py-3 font-body font-medium tracking-wide transition-colors group-hover:bg-terracotta-hover">
              Register for The Financial Masterclass →
            </div>
          </Link>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.04 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="md:col-span-6 order-1 md:order-2"
      >
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] bg-forest/10 -z-10 rotate-2" />
          <BWCarousel />
          <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-white/70 rounded-2xl px-5 py-4 border border-white/60">
            <p className="font-heading text-2xl text-ink leading-none">{PASTOR_NAME}</p>
            <p className="overline text-terracotta mt-1.5">Apostle · Spiritual Father · Teacher</p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Engagements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/engagements")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (d) => {
    const dt = new Date(d + "T00:00:00");
    return {
      month: dt.toLocaleString("en-US", { month: "short" }).toUpperCase(),
      day: dt.getDate(),
    };
  };

  return (
    <section id="engagements" className="py-20 md:py-32" data-testid="engagements-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <p className="overline text-terracotta">Upcoming Engagements</p>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-ink">
              Where we'll <span className="italic">gather</span> next
            </h2>
          </div>
          <p className="font-body text-ink-soft max-w-sm">
            Come say hello in person. Every gathering is open — bring a friend, bring
            your questions, bring yourself.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-terracotta" />
          </div>
        ) : (
          <div className="divide-y divide-black/10 border-t border-black/10">
            {items.map((e, i) => {
              const d = fmtDate(e.date);
              return (
                <motion.div
                  key={e.id}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  data-testid={`engagement-item-${i}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-8"
                >
                  <div className="md:col-span-2 flex md:flex-col items-baseline md:items-start gap-2">
                    <span className="overline text-terracotta">{d.month}</span>
                    <span className="font-heading text-5xl leading-none text-ink">{d.day}</span>
                  </div>
                  <div className="md:col-span-7">
                    <h3 className="font-heading text-3xl text-ink leading-tight">{e.title}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-body text-sm text-ink-soft">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={15} /> {e.location}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays size={15} /> {e.time}
                      </span>
                    </div>
                    <p className="mt-2 font-body text-ink-soft">{e.description}</p>
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <a
                      href={e.rsvp_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`engagement-rsvp-${i}`}
                      className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 font-medium text-ink transition-all group-hover:border-terracotta group-hover:text-terracotta group-hover:-translate-y-0.5"
                    >
                      RSVP <ArrowUpRight size={16} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const Devotional = () => {
  const [dev, setDev] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/devotional/today")
      .then((r) => setDev(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-20 md:py-28" data-testid="devotional-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 md:gap-12 items-stretch">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="md:col-span-5"
        >
          <img
            src={IMAGES.devotional}
            alt="Morning devotional"
            className="w-full h-full min-h-[280px] object-cover rounded-3xl shadow-lg"
          />
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="md:col-span-7 relative rounded-3xl bg-sand-alt border-l-4 border-terracotta p-8 md:p-12 flex flex-col justify-center"
          data-testid="devotional-card"
        >
          <div className="flex items-center gap-2 overline text-terracotta">
            <BookOpen size={16} /> Today's Devotional
          </div>

          {loading ? (
            <div className="flex items-center gap-2 mt-8 text-ink-soft">
              <Loader2 className="animate-spin" size={18} /> Preparing today's reflection…
            </div>
          ) : dev ? (
            <>
              <h3 className="mt-4 font-heading text-4xl md:text-5xl tracking-tight text-ink">
                {dev.title}
              </h3>
              <p className="mt-6 font-heading italic text-2xl md:text-3xl leading-snug text-forest">
                <Quote className="inline mr-2 -mt-2 text-terracotta" size={24} />
                {dev.verse_text}
              </p>
              <p className="mt-2 overline text-ink-soft">{dev.verse}</p>
              <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
                {dev.reflection}
              </p>
            </>
          ) : (
            <p className="mt-6 text-ink-soft">A reflection will appear here soon.</p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email, name: name || null });
      toast.success("Welcome to the circle! Check your inbox soon.");
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="newsletter" className="bg-charcoal grain text-sand" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <p className="overline text-terracotta">The Weekly Letter</p>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            A little <span className="italic text-terracotta">encouragement</span>,
            every week.
          </h2>
          <p className="mt-6 font-body text-sand/70 max-w-md leading-relaxed">
            No spam, no noise — just a personal note from me, upcoming gatherings, and a
            thought to carry into your week.
          </p>
        </div>

        <form onSubmit={submit} className="md:col-span-6 md:pl-8" data-testid="newsletter-form">
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              data-testid="newsletter-name-input"
              className="w-full rounded-2xl bg-white/5 border border-white/15 px-5 py-4 font-body text-sand placeholder:text-sand/40 focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="newsletter-email-input"
              className="w-full rounded-2xl bg-white/5 border border-white/15 px-5 py-4 font-body text-sand placeholder:text-sand/40 focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="newsletter-submit"
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-terracotta px-7 py-4 font-medium text-white transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-sand"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : "Subscribe"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const CommunityTeaser = () => (
  <section className="py-20 md:py-28" data-testid="community-teaser">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-center">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="md:col-span-6"
      >
        <img
          src={IMAGES.community}
          alt="Community gathering"
          className="w-full h-[360px] object-cover rounded-3xl shadow-lg"
        />
      </motion.div>
      <motion.div
        variants={fadeUp}
        custom={1}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="md:col-span-6 md:pl-8"
      >
        <p className="overline text-terracotta">The Community</p>
        <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-ink">
          You were never meant to walk <span className="italic">alone</span>.
        </h2>
        <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-lg">
          Our community chat is a warm, open room — a place to share a prayer request,
          celebrate small wins, or simply say hello. Pull up a chair.
        </p>
        <Link
          to="/chat"
          data-testid="teaser-chat-btn"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sand font-medium transition-all hover:bg-forest-hover hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-terracotta"
        >
          Enter the Community Chat <ArrowUpRight size={18} />
        </Link>
      </motion.div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Engagements />
      <Devotional />
      <CommunityTeaser />
      <Newsletter />
    </div>
  );
}
